import { ChangeDetectionStrategy, Component, Signal, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params } from '@angular/router';

import { catchError, combineLatest, map, of } from 'rxjs';

import { LocationRouteCffService } from '@fcom/core';
import { GlobalBookingTravelClass, TripType } from '@fcom/core/interfaces';
import { LanguageService } from '@fcom/ui-translate';
import { PriceCalendarCTAParams, PriceCalendarParams } from '@fcom/common/interfaces';
import { PriceCalendarService, ScheduleBoundsState } from '@fcom/common';
import { isShortHaulFlight } from '@fcom/common/utils';
import { transformLocationCode } from '@fcom/ond-marketing-landing-core/services';

@Component({
  selector: 'cms-calendar-schedule',
  templateUrl: 'cms-calendar-schedule.component.html',
  styleUrls: ['cms-calendar-schedule.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CmsCalendarScheduleComponent {
  private route = inject(ActivatedRoute);
  private priceCalendarService = inject(PriceCalendarService);
  private locationRouteCffService = inject(LocationRouteCffService);
  private languageService = inject(LanguageService);

  private readonly routeParams: Signal<Params> = toSignal(this.route.params, { initialValue: {} as Params });

  readonly origin = computed<string>(() => transformLocationCode(this.routeParams()['originLocationCode']));
  readonly destination = computed<string>(() => transformLocationCode(this.routeParams()['destinationLocationCode']));

  private readonly tripType = signal<TripType>(TripType.ONEWAY);

  constructor() {
    effect((onCleanup) => {
      const origin = this.origin();
      const destination = this.destination();
      if (!origin || !destination) {
        this.tripType.set(TripType.ONEWAY);
        return;
      }
      const locale = this.languageService.localeValue;
      const subscription = combineLatest([
        this.locationRouteCffService.bestGuessFor(origin, locale),
        this.locationRouteCffService.bestGuessFor(destination, locale),
      ])
        .pipe(
          map(([originLocation, destinationLocation]) =>
            isShortHaulFlight(originLocation, destinationLocation) ? TripType.ONEWAY : TripType.RETURN
          ),
          catchError(() => of(TripType.ONEWAY))
        )
        .subscribe((tripType) => this.tripType.set(tripType));

      onCleanup(() => subscription.unsubscribe());
    });
  }

  // Route-derived params passed to fin-price-calendar
  readonly openWithParams = computed<PriceCalendarParams | null>(() => {
    const origin = this.origin();
    const destination = this.destination();
    if (!origin || !destination) {
      return null;
    }
    return {
      origin,
      destination,
      tripType: this.tripType(),
      travelClass: GlobalBookingTravelClass.ECONOMY,
      paxAmount: { adults: 1, c15s: 0, children: 0, infants: 0 }, // default to 1 adult, no children/infants
    };
  });

  private readonly departureBoundsState: Signal<ScheduleBoundsState> = toSignal(
    this.priceCalendarService.scheduleBounds$,
    { initialValue: { loading: false, bounds: [] } }
  );
  readonly departureBounds = computed(() => this.departureBoundsState().bounds);
  readonly departureLoading = computed(() => this.departureBoundsState().loading);

  private readonly returnBoundsState: Signal<ScheduleBoundsState> = toSignal(
    this.priceCalendarService.returnScheduleBounds$,
    { initialValue: { loading: false, bounds: [] } }
  );
  readonly returnBounds = computed(() => this.returnBoundsState().bounds);
  readonly returnLoading = computed(() => this.returnBoundsState().loading);

  private readonly travelDates = toSignal(this.priceCalendarService.travelDates$);
  readonly hasSelectedDate = computed(() => !!this.travelDates()?.departureDate);
  readonly hasReturnDate = computed(() => !!this.travelDates()?.returnDate);

  // Service's live trip type — flips to RETURN when the user clicks "+Add return" on a one-way calendar.
  private readonly liveTripType = toSignal(this.priceCalendarService.tripType$, { initialValue: TripType.ONEWAY });

  readonly showReturnTab = computed(
    () => this.tripType() === TripType.RETURN || this.liveTripType() === TripType.RETURN
  );

  onBookFlights(params: PriceCalendarCTAParams): void {
    this.priceCalendarService.priceCalendarCTAClicked(params);
  }
}
